// Central content store for QuentinYoung.ca
// All copy is sourced from / faithful to the project specification.
// No fabricated dates, outcomes, clients, testimonials, or third-party endorsements.
// The site is intentionally a knowledge graph. Every entry links to other entries.

export const CONTACT_EMAIL = "hello@quentinyoung.ca";

export const LOGOS = {
  compass: "https://customer-assets.emergentagent.com/job_the-compass-1/artifacts/aplts43w_quentin%20young%20compass%20logo.png",
  medallion: "https://customer-assets.emergentagent.com/job_the-compass-1/artifacts/wzt0o9mc_quentin%20young%20medallion%20black%20bg.jpg",
  wordmark: "https://customer-assets.emergentagent.com/job_the-compass-1/artifacts/nc7drpor_Quentin%20Young%201.png",
  wordmarkAlt: "https://customer-assets.emergentagent.com/job_the-compass-1/artifacts/tsl4q4oz_Quentin%20Young%202.png",
};

export const PORTRAITS = {
  standing: "https://customer-assets.emergentagent.com/job_the-compass-1/artifacts/p31h5a5o_1d1f2294-7f7c-4ff5-8cf6-a7477ec83e7d.jpg",
  threeQuarter: "https://customer-assets.emergentagent.com/job_the-compass-1/artifacts/w8pxuo8h_9ddf5298-3fcb-47e7-ac5a-304682d65c86%20%281%29.jpg",
  seatedThinker: "https://customer-assets.emergentagent.com/job_the-compass-1/artifacts/xubhrpsw_01ab0078-65fd-45cf-83e3-f0671511c114.jpg",
  seatedWindow: "https://customer-assets.emergentagent.com/job_the-compass-1/artifacts/o3gifc0r_cebd1a82-9f51-4a37-a9d9-929682f0f244.jpg",
  centered: "https://customer-assets.emergentagent.com/job_the-compass-1/artifacts/whgr396j_fc489105-0b47-4501-9624-2ca12cda3f68%20%281%29.jpg",
};

export const TEXTURES = {
  stairs: "https://images.unsplash.com/photo-1472803828399-39d4ac53c6e5?auto=format&fit=crop&w=2000&q=80",
  buildingDetail: "https://images.unsplash.com/photo-1665779736808-047a6bbf43a0?auto=format&fit=crop&w=2000&q=80",
  corridor: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=2000&q=80",
  emptyRoom: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=80",
  chairs: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=2000&q=80",
};

export const NAV = [
  { label: "About", to: "/about" },
  { label: "Thinking", to: "/thinking" },
  { label: "Building", to: "/building" },
  { label: "Philosophy", to: "/philosophy" },
  { label: "Artifacts", to: "/artifacts" },
  { label: "Acknowledgements", to: "/acknowledgements" },
  { label: "Media", to: "/media" },
  { label: "Contact", to: "/contact" },
];

export const DOMAINS = [
  { slug: "human-nature", name: "Human Nature", number: "01" },
  { slug: "perception", name: "Perception", number: "02" },
  { slug: "purpose", name: "Purpose", number: "03" },
  { slug: "achievement", name: "Achievement", number: "04" },
  { slug: "culture", name: "Culture", number: "05" },
  { slug: "leadership", name: "Leadership", number: "06" },
  { slug: "systems", name: "Systems", number: "07" },
  { slug: "organizations", name: "Organizations", number: "08" },
  { slug: "civilization", name: "Civilization", number: "09" },
  { slug: "craft", name: "Craft", number: "10" },
  { slug: "relationships", name: "Relationships", number: "11" },
];

export const PILLARS = [
  { slug: "architect", number: "01", name: "Architect", definition: "Design, foresight, systems thinking, long-term planning, pattern recognition, intentional construction.", rejects: "Reactionary thinking. Accidental outcomes. Building without purpose.", absence: "Without it, people become busy instead of effective." },
  { slug: "builder", number: "02", name: "Builder", definition: "Execution. Demonstration. Ownership. Persistence.", rejects: "Endless planning. Performative intelligence. Excuses. Untested theories.", absence: "Without it, ideas remain fantasies." },
  { slug: "observer", number: "03", name: "Observer", definition: "Curiosity, self-reflection, awareness, learning.", rejects: "Assumption. Certainty without evidence. Ego-driven decisions.", absence: "Without it, the same mistakes repeat." },
  { slug: "steward", number: "04", name: "Steward", definition: "Responsibility, respect, accountability, dignity, service.", rejects: "Exploitation. Entitlement. Neglect. Self-interest at the expense of the collective.", absence: "Without it, trust disappears and culture decays." },
  { slug: "catalyst", number: "05", name: "Catalyst", definition: "Growth, momentum, adaptation, evolution, change.", rejects: "Stagnation. Complacency. Comfort as a default.", absence: "Without it, potential remains dormant." },
  { slug: "compass", number: "06", name: "Compass", definition: "Purpose, meaning, alignment, shared belief, legacy.", rejects: "Aimlessness. Drift. Empty achievement. Success without fulfillment.", absence: "Without it, outcomes can still feel hollow." },
];

export const OBSERVATIONS = [
  {
    slug: "behavior-follows-reward",
    domain: "human-nature",
    tier: "cornerstone",
    pillar: "observer",
    headline: "People rarely behave according to what they believe.",
    body: "They behave according to what they reward.",
    note: "What an organization tolerates becomes what it teaches. What it teaches becomes what it is.",
    relatedObservations: ["systems-produce-results", "people-become-environments", "details-reveal-values"],
    relatedBuilding: ["openline", "operating-class", "bristol-auto"],
    relatedArtifacts: ["early-proposal", "field-notes-binder"],
    relatedTimeline: ["operating"],
  },
  {
    slug: "distance-distorts-reality",
    domain: "perception",
    tier: "cornerstone",
    pillar: "observer",
    headline: "Distance distorts reality.",
    body: "The further the decision sits from the work, the more confident, and the more wrong, it tends to become.",
    relatedObservations: ["leadership-creates-conditions", "systems-produce-results"],
    relatedBuilding: ["operating-class", "openline"],
    relatedArtifacts: ["first-deck"],
  },
  {
    slug: "endurance-needs-a-reason",
    domain: "purpose",
    tier: "cornerstone",
    pillar: "compass",
    headline: "People can endure almost anything when they know why they are doing it.",
    body: "Remove the reason and the strongest team grows fragile by Tuesday.",
    relatedObservations: ["cooperation-is-the-advantage", "leadership-creates-conditions"],
    relatedBuilding: ["fundroute", "everyday-emissary"],
  },
  {
    slug: "potential-and-performance",
    domain: "achievement",
    tier: "cornerstone",
    pillar: "catalyst",
    headline: "Potential and performance are distant relatives.",
    body: "They occasionally show up at the same table. Most of the time they live on different streets.",
    relatedObservations: ["people-become-environments", "details-reveal-values"],
    relatedBuilding: ["talentroute", "operating-class"],
  },
  {
    slug: "people-become-environments",
    domain: "culture",
    tier: "cornerstone",
    pillar: "steward",
    headline: "People become their environments.",
    body: "Standards are rarely chosen. They are absorbed.",
    relatedObservations: ["details-reveal-values", "leadership-creates-conditions"],
    relatedBuilding: ["operating-class", "everyday-emissary"],
  },
  {
    slug: "leadership-creates-conditions",
    domain: "leadership",
    tier: "cornerstone",
    pillar: "architect",
    headline: "Leadership creates conditions. Conditions create outcomes.",
    body: "The outcomes you blame your team for are usually the conditions you forgot to design.",
    relatedObservations: ["systems-produce-results", "people-become-environments"],
    relatedBuilding: ["operating-class", "openline", "revkontrol"],
    relatedArtifacts: ["early-proposal"],
  },
  {
    slug: "systems-produce-results",
    domain: "systems",
    tier: "cornerstone",
    pillar: "architect",
    headline: "Every system is designed to produce the results it currently produces.",
    body: "The kindest read of a bad outcome is that someone, somewhere, set it up that way on purpose.",
    relatedObservations: ["organizations-fail-gradually", "leadership-creates-conditions"],
    relatedBuilding: ["revkontrol", "lotwise", "fixbook"],
  },
  {
    slug: "organizations-fail-gradually",
    domain: "organizations",
    tier: "cornerstone",
    pillar: "steward",
    headline: "Organizations rarely fail suddenly.",
    body: "They fail gradually, then visibly.",
    relatedObservations: ["systems-produce-results", "trust-compounds"],
    relatedBuilding: ["bristol-auto", "revkontrol"],
    relatedArtifacts: ["discarded-mark"],
  },
  {
    slug: "cooperation-is-the-advantage",
    domain: "civilization",
    tier: "cornerstone",
    pillar: "compass",
    headline: "Humanity's greatest advantage is not intelligence.",
    body: "It is cooperation. Everything we have built is a coordination problem solved at scale.",
    relatedObservations: ["endurance-needs-a-reason", "trust-compounds"],
    relatedBuilding: ["marshal-line", "dandylads"],
  },
  {
    slug: "details-reveal-values",
    domain: "craft",
    tier: "cornerstone",
    pillar: "builder",
    headline: "Details reveal values.",
    body: "What an organization does when no one is watching is what an organization actually is.",
    relatedObservations: ["behavior-follows-reward", "people-become-environments"],
    relatedBuilding: ["normal-basics", "fixbook", "everyday-emissary"],
    relatedArtifacts: ["first-wordmark"],
  },
  {
    slug: "trust-compounds",
    domain: "relationships",
    tier: "cornerstone",
    pillar: "steward",
    headline: "Trust compounds quickly. So does its absence.",
    body: "A relationship spends years climbing and a single afternoon falling.",
    relatedObservations: ["cooperation-is-the-advantage", "organizations-fail-gradually"],
    relatedBuilding: ["openline", "marshal-line"],
  },
];

export const FIELD_NOTES = [
  { slug: "incentives-over-instructions", domain: "human-nature", pillar: "observer", text: "Incentives are louder than instructions. Always have been.", relatedObservations: ["behavior-follows-reward"] },
  { slug: "performance-is-earned", domain: "achievement", pillar: "builder", text: "Performance is earned. Position is borrowed.", relatedObservations: ["potential-and-performance"] },
  { slug: "culture-is-a-decision", domain: "culture", pillar: "steward", text: "Culture is not a value on a wall. It is the next decision your team is about to make without you.", relatedObservations: ["people-become-environments"] },
  { slug: "comfort-zones", domain: "human-nature", pillar: "catalyst", text: "Comfort zones reveal more than résumés. People become what they are willing to tolerate.", relatedObservations: ["people-become-environments"] },
  { slug: "ending-matters", domain: "relationships", pillar: "steward", text: "People remember how things ended. They rarely remember how they began.", relatedObservations: ["trust-compounds"] },
  { slug: "the-quiet-work", domain: "craft", pillar: "builder", text: "Most of the work that matters is invisible until it is missing.", relatedObservations: ["details-reveal-values"] },
  { slug: "raise-the-water", domain: "leadership", pillar: "catalyst", text: "Raise the water level. Do not lower the bar.", relatedObservations: ["leadership-creates-conditions"] },
  { slug: "customer-first", domain: "organizations", pillar: "compass", text: "A business that talks more about itself than its customer has already started losing.", relatedObservations: ["organizations-fail-gradually"] },
];

export const BUILDING = [
  { slug: "kostaja", name: "Kostaja Ltd.", category: "Ventures", status: "Active", role: "Owner and operator", url: "https://kostaja.ca/", pillar: "architect", lead: "The company through which the work is built and held.", body: "An ongoing venture Quentin owns and operates. Full case study will be published when the work earns it.", relatedObservations: ["details-reveal-values"], relatedArtifacts: [] },
  { slug: "refreshers", name: "Refreshers", category: "Ventures", status: "Active", pillar: "catalyst", lead: "A venture in development.", body: "An ongoing venture. Detail will be added as the work earns permanence.", relatedObservations: ["people-become-environments"], relatedArtifacts: [] },
  { slug: "revkontrol", name: "RevKontrol", category: "Ventures", status: "Active", pillar: "architect", lead: "Revenue operations and control systems for operators.", body: "Working notes available privately. Public detail will follow when the work justifies it.", relatedObservations: ["systems-produce-results", "leadership-creates-conditions"], relatedArtifacts: ["first-deck"] },
  { slug: "fundroute", name: "FundRoute", category: "Ventures", status: "Active", pillar: "compass", lead: "Capital pathways for builders.", body: "An ongoing venture. Public detail will follow.", relatedObservations: ["endurance-needs-a-reason"], relatedArtifacts: [] },
  { slug: "talentroute", name: "TalentRoute", category: "Ventures", status: "Active", pillar: "steward", lead: "Connecting people to the conditions where they can do their best work.", body: "An ongoing venture.", relatedObservations: ["potential-and-performance", "people-become-environments"], relatedArtifacts: [] },
  { slug: "everyday-emissary", name: "Everyday Emissary", category: "Ventures", status: "Active", pillar: "steward", lead: "On the small acts that carry an institution.", body: "An ongoing venture.", relatedObservations: ["details-reveal-values", "people-become-environments"], relatedArtifacts: [] },
  { slug: "normal-basics", name: "Normal Basics", category: "Ventures", status: "Active", pillar: "builder", lead: "A study in restraint.", body: "An ongoing venture.", relatedObservations: ["details-reveal-values"], relatedArtifacts: [] },
  { slug: "lotwise", name: "LotWise Auto", category: "Ventures", status: "Active", role: "Owner and operator", url: "https://lotwiseauto.ca/", pillar: "architect", lead: "Inventory intelligence for automotive retail.", body: "A venture Quentin owns and operates.", relatedObservations: ["systems-produce-results"], relatedArtifacts: [] },
  { slug: "lotwise-home", name: "LotWise Home", category: "Ventures", status: "Active", role: "Owner and operator", url: "https://lotwisehome.ca/", pillar: "architect", lead: "Inventory intelligence for home and property retail.", body: "A venture Quentin owns and operates.", relatedObservations: ["systems-produce-results"], relatedArtifacts: [] },
  { slug: "openline", name: "OpenLine", category: "Ventures", status: "Active", pillar: "steward", lead: "An honest channel between leadership and the floor.", body: "An ongoing venture.", relatedObservations: ["distance-distorts-reality", "trust-compounds", "behavior-follows-reward"], relatedArtifacts: [] },
  { slug: "agent-marketing", name: "Agent Marketing", category: "Ventures", status: "Active", role: "Owner and operator", url: "https://agentmark.ca/", pillar: "builder", lead: "Marketing built around the people doing the actual work.", body: "A venture Quentin owns and operates.", relatedObservations: ["details-reveal-values"], relatedArtifacts: [] },
  { slug: "fixbook", name: "FixBook", category: "Ventures", status: "Active", pillar: "observer", lead: "A workshop's memory.", body: "An ongoing venture.", relatedObservations: ["systems-produce-results", "details-reveal-values"], relatedArtifacts: [] },
  { slug: "autowa", name: "Autowa", category: "Ventures", status: "Active", pillar: "steward", lead: "Vehicles, owners, and the long relationship between them.", body: "An ongoing venture.", relatedObservations: ["trust-compounds"], relatedArtifacts: [] },
  { slug: "operating-class", name: "Operating Class", category: "Ventures", status: "Active", role: "Owner and operator", url: "https://operatingclass.com/", pillar: "observer", lead: "Notes from the people who actually run the room.", body: "A venture Quentin owns and operates.", relatedObservations: ["distance-distorts-reality", "leadership-creates-conditions", "behavior-follows-reward"], relatedArtifacts: ["field-notes-binder"] },
  { slug: "bristol-auto", name: "Bristol Auto Performance", category: "Creative Work", status: "Client", role: "Brand and website", url: "https://bristolauto.ca/", pillar: "steward", lead: "Brand and website by Quentin Young.", body: "Creative work behind the brand and website of Bristol Auto Performance. Case study in preparation.", relatedObservations: ["behavior-follows-reward", "organizations-fail-gradually"], relatedArtifacts: [] },
  { slug: "wings-n-things", name: "Wings N Things", category: "Creative Work", status: "Client", pillar: "builder", lead: "Brand and creative work.", body: "Case study in preparation.", relatedObservations: [], relatedArtifacts: [] },
  { slug: "deanos-chicken", name: "Deano's Chicken", category: "Creative Work", status: "Client", pillar: "builder", lead: "Brand and creative work.", body: "Case study in preparation.", relatedObservations: [], relatedArtifacts: [] },
  { slug: "murray-mortgages", name: "Murray Mortgages", category: "Creative Work", status: "Client", role: "Brand and website", url: "https://murraymortgages.ca/", pillar: "builder", lead: "Brand and website by Quentin Young.", body: "Creative work behind the brand and website of Murray Mortgages. Case study in preparation.", relatedObservations: [], relatedArtifacts: [] },
  { slug: "young-subdivision", name: "Young Subdivision", category: "Creative Work", status: "Client / Long-horizon", pillar: "compass", lead: "Brand and creative work. Also a strategic long-horizon interest.", body: "Documentation in preparation.", relatedObservations: [], relatedArtifacts: [] },
  { slug: "marshal-line", name: "Marshal Line", category: "Collaborations", status: "Friendly", pillar: "compass", lead: "Building alongside good people.", body: "Working details to follow when both parties are ready to share.", relatedObservations: ["cooperation-is-the-advantage", "trust-compounds"], relatedArtifacts: [] },
  { slug: "dandylads", name: "DandyLads", category: "Collaborations", status: "Friendly", pillar: "compass", lead: "Building alongside good people.", body: "Working details to follow.", relatedObservations: ["cooperation-is-the-advantage"], relatedArtifacts: [] },
  { slug: "remembrand", name: "Remembrand", category: "Collaborations", status: "Friendly", pillar: "compass", lead: "Building alongside good people.", body: "Working details to follow.", relatedObservations: [], relatedArtifacts: [] },
  { slug: "young-subdivision-strategic", name: "Young Subdivision (Strategic)", category: "Strategic Interests", status: "Long-horizon", pillar: "compass", lead: "A long-horizon interest where future ownership or deeper participation is possible.", body: "Noted publicly as a strategic interest, not a commitment.", relatedObservations: [], relatedArtifacts: [] },
];

export const PHILOSOPHY_LENSES = [
  { name: "People Matter", body: "Every system, structure, and result eventually returns to a person." },
  { name: "Responsibility Compounds", body: "Whatever a person carries well is given more. Whatever they neglect quietly accumulates." },
  { name: "Capability Is Earned", body: "It cannot be bought, declared, or inherited. It is paid for in repetition." },
  { name: "Culture Is Destiny", body: "What is tolerated will be repeated. What is repeated becomes the future." },
  { name: "The Long Game Always Wins", body: "Short-term wins are interest payments. The principal is built over years." },
  { name: "Self-Reflection Is A Competitive Advantage", body: "Most people will not look honestly at themselves. Those who do gain ground every year." },
  { name: "Systems Exist To Serve Humans", body: "When this is reversed, decay has already started." },
];

export const REASONING_MODEL = [
  { step: "01", name: "Purpose", note: "Why does this exist?" },
  { step: "02", name: "People", note: "Who is actually here?" },
  { step: "03", name: "Culture", note: "What gets rewarded when nobody is looking?" },
  { step: "04", name: "Systems", note: "What does the machine produce on autopilot?" },
  { step: "05", name: "Leadership", note: "Who is creating the conditions?" },
  { step: "06", name: "Performance", note: "What is actually happening, not what is reported?" },
  { step: "07", name: "Evolution", note: "What is this becoming?" },
];

export const LESSONS = [
  { slug: "grocery-store", era: "First job", title: "Grocery store", lesson: "Individual behavior affects the system. A misplaced cart is a small thing until it is the only thing a customer remembers.", pillar: "steward", relatedObservations: ["details-reveal-values"] },
  { slug: "summer-camp", era: "Summer camp", title: "Accountability", lesson: "Personal choices cannot weaken collective responsibility. The youngest watch closely.", pillar: "steward", relatedObservations: ["details-reveal-values"] },
  { slug: "research-institute", era: "Research institute", title: "The unseen backbone", lesson: "Respect the people whose names do not appear on the paper. The work depends on them.", pillar: "steward", relatedObservations: ["details-reveal-values", "cooperation-is-the-advantage"] },
  { slug: "short-term", era: "Short-term roles", title: "Quitting", lesson: "The short-term want is rarely worth the long-game cost. Endings are remembered longer than beginnings.", pillar: "compass", relatedObservations: ["trust-compounds"] },
  { slug: "restaurant", era: "Restaurant", title: "Resignation", lesson: "People remember how you leave. Sometimes more than how you arrived.", pillar: "steward", relatedObservations: ["trust-compounds"] },
  { slug: "soccer", era: "Soccer team", title: "Earned position", lesson: "Performance is earned. Position is borrowed. Neither is a personality.", pillar: "builder", relatedObservations: ["potential-and-performance"] },
  { slug: "cook", era: "Cook job", title: "Comfort zones", lesson: "Comfort zones reveal more about a person than résumés do.", pillar: "catalyst", relatedObservations: ["people-become-environments"] },
  { slug: "promotion", era: "First promotion", title: "Self-reflection", lesson: "The compounding asset nobody puts on their LinkedIn.", pillar: "observer", relatedObservations: ["potential-and-performance"] },
  { slug: "recruitment", era: "Recruitment", title: "Dignity", lesson: "How people are treated at their most vulnerable moments shapes whether they ever return.", pillar: "steward", relatedObservations: ["trust-compounds"] },
  { slug: "corporate", era: "Corporate politics", title: "Incentives", lesson: "Understand the incentives in the room. Then protect the original contribution from them.", pillar: "observer", relatedObservations: ["behavior-follows-reward"] },
  { slug: "toxic-small-biz", era: "A toxic small business", title: "Selfish incentives", lesson: "Culture can rot from the top while the bottom keeps working. It does not last.", pillar: "steward", relatedObservations: ["organizations-fail-gradually", "people-become-environments"] },
  { slug: "building-again", era: "Building again", title: "The customer journey", lesson: "When the conversation centers on the customer instead of the self, almost everything else organizes itself.", pillar: "compass", relatedObservations: ["organizations-fail-gradually"] },
  { slug: "tailored", era: "Tailored applications", title: "Raise the water level", lesson: "Make the room better than you found it without becoming a threat to anyone in it.", pillar: "catalyst", relatedObservations: ["leadership-creates-conditions"] },
];

export const TIMELINE = [
  { slug: "early", year: "Early", title: "Frontline", note: "Grocery store. Hospitality. First lessons in how systems treat people and how people treat systems.", relatedLessons: ["grocery-store", "summer-camp"], relatedObservations: ["details-reveal-values"] },
  { slug: "studies", year: "Studies", title: "Research institute", note: "Exposure to academic rigor. Quiet respect for the people who hold the backbone.", relatedLessons: ["research-institute"], relatedObservations: ["cooperation-is-the-advantage"] },
  { slug: "operating", year: "Operating", title: "Automotive and operations", note: "Bristol Auto. Hands-on with the realities of running customer-facing operations.", relatedBuilding: ["bristol-auto"], relatedObservations: ["behavior-follows-reward"] },
  { slug: "building", year: "Building", title: "Ventures, clients, collaborations", note: "Kostaja. RevKontrol. FundRoute. Marshal Line. DandyLads. Remembrand. A growing slate of work.", relatedBuilding: ["kostaja", "revkontrol", "fundroute", "marshal-line", "dandylads", "remembrand"] },
  { slug: "now", year: "Now", title: "The Pursuit", note: "Documenting the conditions that let individuals, teams, and organizations become more than they otherwise would have been.", relatedObservations: ["endurance-needs-a-reason", "cooperation-is-the-advantage"] },
];

// Expanded artifact model. Placeholders intentionally minimal until real assets arrive.
// Each artifact supports: image, category, era, context, whatChanged, taught, relatedProject, relatedObservation, relatedPillar.
export const ARTIFACT_CATEGORIES = [
  { slug: "logos", name: "Logos and Marks" },
  { slug: "drafts", name: "Drafts and Concepts" },
  { slug: "documents", name: "Documents" },
  { slug: "screens", name: "Screens and Sites" },
  { slug: "notes", name: "Working Notes" },
];

export const ARTIFACTS = [
  {
    slug: "first-wordmark",
    title: "First Wordmark",
    category: "logos",
    era: "Early",
    published: true,
    image: null,
    context: "The original attempt at a mark. Not the version in use today. Kept because it still explains the direction.",
    whatChanged: "The compass evolved. The letterforms were retired. The point of view survived.",
    taught: "What you keep is more telling than what you launch.",
    relatedProject: null,
    relatedObservation: "details-reveal-values",
    relatedPillar: "builder",
  },
  {
    slug: "early-proposal",
    title: "Early Client Proposal",
    category: "documents",
    era: "Early",
    published: true,
    image: null,
    context: "A proposal written when the instinct was to lead with the service rather than the customer.",
    whatChanged: "The order was reversed. Customer, then service, then price. Read rates changed.",
    taught: "Lead with the customer. The rest is negotiation.",
    relatedProject: "revkontrol",
    relatedObservation: "leadership-creates-conditions",
    relatedPillar: "compass",
  },
  {
    slug: "discarded-mark",
    title: "Discarded Mark",
    category: "logos",
    era: "Mid",
    published: true,
    image: null,
    context: "Three months of work that did not earn permanence. Kept close as a discipline.",
    whatChanged: "The idea moved from decoration to structural. The final mark was earned from what this taught.",
    taught: "The best marks are refused as often as approved.",
    relatedProject: null,
    relatedObservation: "organizations-fail-gradually",
    relatedPillar: "observer",
  },
  {
    slug: "first-deck",
    title: "First Investor Deck",
    category: "documents",
    era: "Mid",
    published: true,
    image: null,
    context: "A document that aged faster than it deserved. Saved as a reminder that ambition without evidence is fragile.",
    whatChanged: "Metrics moved into the front half. Vision moved to the back. The order matters.",
    taught: "Evidence first. Vision second. The room can tell the difference.",
    relatedProject: "revkontrol",
    relatedObservation: "distance-distorts-reality",
    relatedPillar: "architect",
  },
  {
    slug: "field-notes-binder",
    title: "Field Notes Binder",
    category: "notes",
    era: "Ongoing",
    published: true,
    image: null,
    context: "Where most ideas die quietly. Only the ones that survive contact with reality earn their way out.",
    whatChanged: "The bar for permanence rose. The observation archive on this site began here.",
    taught: "Not every observation becomes more. Ideas grow only when they earn it.",
    relatedProject: "operating-class",
    relatedObservation: "behavior-follows-reward",
    relatedPillar: "observer",
  },
];

// Acknowledgements. Text-first, permission-aware. Real names will only appear with explicit consent.
export const ACKNOWLEDGEMENTS = {
  intro: "No one becomes who they are alone. This page is deliberate. Names will appear here only with consent, in their own time, and never in service of implied endorsement.",
  mentors: [
    { name: "Family", relationship: "First teachers", note: "On standards, exposure, and the long horizon. Names withheld until permission is given." },
    { name: "Operators and owners encountered along the way", relationship: "Mentors of the working kind", note: "People who taught more by how they ran the room than by what they said." },
  ],
  collaborators: [
    { name: "Marshal Line", relationship: "Collaboration", note: "Building alongside." },
    { name: "DandyLads", relationship: "Collaboration", note: "Building alongside." },
    { name: "Remembrand", relationship: "Collaboration", note: "Building alongside." },
  ],
  influences: [
    { name: "Books, conversations, and rooms", relationship: "Influence", note: "A formal reading list and citation page will be added when it earns permanence." },
  ],
  partners: [
    { name: "Clients and partners", relationship: "Partners", note: "Named publicly only with explicit permission." },
  ],
};

export const MEDIA = [];

export const COACHING = {
  eyebrow: "Coaching",
  h1: "Direct work on the conditions that shape better outcomes.",
  opening: [
    "Some people need strategy.",
    "Some need structure.",
    "Some need sharper positioning.",
    "Some need someone who can see the pattern before they can name it.",
  ],
  lead:
    "Coaching with Quentin Young is direct, serious, and practical. It is built for people trying to improve how they think, communicate, decide, build, sell, lead, and move.",
  connective:
    "The work sits beside the same themes that run through the rest of this site: judgment, positioning, execution, self-command, and better systems of action.",
  lanes: [
    {
      slug: "personal",
      number: "01",
      name: "Personal Coaching",
      summary:
        "For ambition, self-direction, and momentum that holds under pressure.",
      body:
        "Confidence, accountability, decision-making, reinvention, identity, and discipline. The work is not motivation. It is building the internal structure that lets a person move deliberately and recover quickly when growth gets uncomfortable.",
      relatedObs: "endurance-needs-a-reason",
    },
    {
      slug: "business",
      number: "02",
      name: "Business Coaching",
      summary:
        "For founders, owners, operators, leaders, and professionals sharpening how they run the whole.",
      body:
        "Priorities, decisions, systems, offers, performance, and operations. The aim is to think more clearly about the business as a whole and to make the next set of decisions with less noise and more evidence.",
      relatedObs: "systems-produce-results",
    },
    {
      slug: "marketing",
      number: "03",
      name: "Marketing Coaching",
      summary:
        "For people and businesses working on how they are understood.",
      body:
        "Positioning, brand, offer, audience, messaging, content, and market presence. Perception is built on purpose or it is left to accident. This work makes it deliberate.",
      relatedObs: "details-reveal-values",
    },
    {
      slug: "sales",
      number: "04",
      name: "Sales Coaching",
      summary:
        "For people who need to communicate value without sounding generic, unsure, or desperate.",
      body:
        "Structuring conversations, qualifying well, and closing with clarity. The goal is confidence that comes from preparation and honesty, not pressure tactics or scripts that everyone can already hear coming.",
      relatedObs: "trust-compounds",
    },
  ],
  howItWorks: [
    "Sessions are one to one and built around the situation in front of you, not a fixed curriculum.",
    "The first conversation is about fit and the real problem, not a pitch.",
    "Work is practical. You leave with clearer thinking and a next move, not homework for its own sake.",
    "Cadence is set by the work. Some situations need a single session. Others need a sustained arrangement.",
  ],
  whoFor: [
    "People building, leading, selling, positioning, or becoming something more serious.",
    "Operators who want a direct read on the pattern, not reassurance.",
    "People willing to change how they think and act, not only what they know.",
  ],
  whoNotFor: [
    "Anyone looking for motivation without change.",
    "Anyone who wants their current thinking confirmed.",
    "Anyone expecting guarantees instead of honest work.",
  ],
  availability: "Available by application, referral, or direct fit.",
  ctaLabel: "Start the conversation",
};

export const SPEAKING = {
  eyebrow: "Speaking",
  h1: "Sharp, human, observational talks for serious rooms.",
  opening:
    "Speaking with Quentin Young is an extension of the thinking on this site, not a separate performance brand. It is built to help a room see people, organizations, culture, leadership, systems, brand, and achievement differently.",
  lead:
    "This is not motivational speaking, and it is not generic business inspiration. It is practical observation delivered plainly, so an audience leaves with a clearer way of seeing rather than a temporary lift.",
  themes: [
    {
      number: "01",
      title: "Human Nature in Organizations",
      body:
        "How people behave inside systems, why incentives matter, how culture shapes conduct, and why organizations often get exactly the behaviour they reward.",
      relatedObs: "behavior-follows-reward",
    },
    {
      number: "02",
      title: "Culture and Leadership",
      body:
        "How environments shape people, how leaders create conditions, how trust compounds, and why culture lives in tolerated behaviour more than stated values.",
      relatedObs: "leadership-creates-conditions",
    },
    {
      number: "03",
      title: "The Pursuit of Collective Human Potential",
      body:
        "The conditions that allow individuals, teams, organizations, and communities to become more than they otherwise would have been.",
      relatedObs: "endurance-needs-a-reason",
    },
    {
      number: "04",
      title: "Potential vs Performance",
      body:
        "Why potential alone is insufficient, what turns capability into output, and why discipline, environment, belief, and accountability decide the difference.",
      relatedObs: "potential-and-performance",
    },
    {
      number: "05",
      title: "Brand, Perception, and Trust",
      body:
        "How perception is built, how trust accumulates, how brands are validated through behaviour, and why details reveal values.",
      relatedObs: "trust-compounds",
    },
    {
      number: "06",
      title: "Systems, Craft, and Execution",
      body:
        "Why outcomes repeat, how systems shape behaviour, why details matter, and how execution becomes evidence.",
      relatedObs: "systems-produce-results",
    },
    {
      number: "07",
      title: "Building, Reinvention, and the Long Game",
      body:
        "Lessons from building ventures, refining identity, reading markets, and choosing long-term value over short-term performance.",
      relatedObs: "organizations-fail-gradually",
    },
  ],
  engagementTypes: [
    "Keynotes",
    "Panels",
    "Fireside conversations",
    "Workshops",
    "Private executive sessions",
    "Internal company sessions",
    "Podcast and interview appearances",
    "Community and educational talks",
  ],
  whoFor: [
    "Leadership teams and organizations examining culture, systems, and conduct.",
    "Founders, operators, and builders thinking about the long game.",
    "Conferences and rooms that prefer substance to spectacle.",
  ],
  leaveWith: [
    "A sharper way of seeing people and the systems they operate inside.",
    "Language for patterns they already sensed but could not name.",
    "Practical questions worth carrying back into the work.",
  ],
  bioShort:
    "Quentin Young is a Canadian entrepreneur, founder, operator, brand strategist, and creative leader who studies the conditions that shape individual and collective potential.",
  ctaLabel: "Discuss a speaking engagement",
};

export const NOW = {
  updated: "February 2026",
  building: [
    "Kostaja Ltd. and the venture stack it holds.",
    "LotWise Auto and LotWise Home.",
    "Operating Class, Agent Marketing, and adjacent client work.",
  ],
  studying: [
    "How organizations get exactly the behaviour they reward.",
    "Perception, brand, and the small decisions that compound into market position.",
  ],
  offering: [
    "Coaching by application, referral, or direct fit.",
    "Speaking for serious rooms.",
  ],
};

export const SITE_META = {
  name: "Quentin Young",
  domain: "quentinyoung.ca",
  tagline: "The Pursuit of Collective Human Potential.",
  intro:
    "The study and pursuit of the conditions that allow individuals, teams, organizations, and societies to become more than they otherwise would have been.",
};

// ==========================================================================
// Lookup helpers, used by pages to build the knowledge graph in JSX.
// ==========================================================================

export function getObservation(slug) { return OBSERVATIONS.find((o) => o.slug === slug); }
export function getBuilding(slug) { return BUILDING.find((b) => b.slug === slug); }
export function getArtifact(slug) { return ARTIFACTS.find((a) => a.slug === slug); }
export function getPillar(slug) { return PILLARS.find((p) => p.slug === slug); }
export function getDomain(slug) { return DOMAINS.find((d) => d.slug === slug); }
export function getFieldNote(slug) { return FIELD_NOTES.find((f) => f.slug === slug); }
export function getLesson(slug) { return LESSONS.find((l) => l.slug === slug); }
export function getTimeline(slug) { return TIMELINE.find((t) => t.slug === slug); }

export function relatedFieldNotesForObservation(obsSlug) {
  return FIELD_NOTES.filter((n) => (n.relatedObservations || []).includes(obsSlug));
}
export function buildingForObservation(obsSlug) {
  return BUILDING.filter((b) => (b.relatedObservations || []).includes(obsSlug));
}
export function artifactsForObservation(obsSlug) {
  return ARTIFACTS.filter((a) => a.relatedObservation === obsSlug);
}
export function observationsForBuilding(buildingSlug) {
  const b = getBuilding(buildingSlug);
  return (b?.relatedObservations || []).map(getObservation).filter(Boolean);
}
export function artifactsForBuilding(buildingSlug) {
  return ARTIFACTS.filter((a) => a.relatedProject === buildingSlug);
}
export function observationsForPillar(pillarSlug) {
  return OBSERVATIONS.filter((o) => o.pillar === pillarSlug);
}
export function buildingForPillar(pillarSlug) {
  return BUILDING.filter((b) => b.pillar === pillarSlug);
}
export function lessonsForPillar(pillarSlug) {
  return LESSONS.filter((l) => l.pillar === pillarSlug);
}
